import React, { useState, useEffect } from "react";
import { useAuthStore } from "../store/authStore";
import { updateUserRequest } from "../service/userService"; 
import { Splash } from "../components/Splash";
import { User, Mail, Lock, ShieldCheck } from "lucide-react";
import { notifyError, notifySuccess } from "../utils/toast";
import { AccountPrintSettings } from "./AccountPrintSettings";
import { IconTextButton } from "../components/ButtonComponents";

export const AccountSettings: React.FC = () => {
  const { user, setUser } = useAuthStore();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
 
  useEffect(() => {
    setName(user?.name || "");
    setEmail(user?.email || "");
  }, [user]);

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); 

    try {
      const updatedUser = await updateUserRequest({ name, email, password });
      setUser(updatedUser); 
      notifySuccess("Hesap bilgileri başarıyla güncellendi!");
      setPassword(""); 
    } catch (err: any) {
      notifyError(err.response?.data?.message || err.message || "Güncelleme başarısız");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative p-4 md:p-8 w-full max-w-7xl mx-auto min-h-screen">
      {loading && <Splash show={true} />}

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-text tracking-tight">Kişisel Profilim</h1>
        <p className="text-text/60 mt-2 text-sm max-w-2xl">
          Kişisel hesap bilgilerinizi, şifrenizi ve cihazınıza özel yazdırma tercihlerinizi bu alandan yönetebilirsiniz.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Sol Kolon: Hesap Bilgileri */}
        <div className="lg:col-span-4 xl:col-span-3 w-full">
          <div className="bg-card rounded-2xl shadow-lg border border-borderColor overflow-hidden transition-all duration-300 relative">
            
            {/* Üst Dekoratif Arka Plan */}
            <div className="h-24 w-full bg-primary/20 relative overflow-hidden">
              <div className="absolute inset-0 bg-primary opacity-10"></div>
            </div>

            <div className="px-6 pb-6 relative">
              {/* Avatar Gösterimi */}
              <div className="w-20 h-20 bg-card rounded-2xl border-4 border-card shadow-lg flex items-center justify-center -mt-10 mb-4 relative z-10 bg-gradient-to-br from-bgAlt to-bg">
                <span className="text-2xl font-bold text-primary uppercase">
                  {name ? name.substring(0, 2) : "US"}
                </span>
              </div>

              <div className="mb-6">
                <h2 className="text-xl font-bold text-text">{user?.name}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <ShieldCheck className="w-4 h-4 text-emerald-500" />
                  <span className="text-xs font-semibold text-text/60 uppercase tracking-wide">
                    {user?.role?.name?.replace('_', ' ')}
                  </span>
                </div>
              </div>

              <form className="flex flex-col gap-5" onSubmit={handleUpdate}>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-text/70 uppercase tracking-wider ml-1">Ad Soyad</label>
                  <div className="relative group">
                    <User className="absolute left-3.5 top-3.5 w-5 h-5 text-text/40 group-focus-within:text-primary transition-colors" />
                    <input
                      type="text"
                      placeholder="Ad Soyad"
                      className="input pl-11 py-3 bg-bgAlt/50 focus:bg-card border-borderColor/50 rounded-xl w-full text-sm font-medium transition-all"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-text/70 uppercase tracking-wider ml-1">E-Posta Adresi</label>
                  <div className="relative group">
                    <Mail className="absolute left-3.5 top-3.5 w-5 h-5 text-text/40 group-focus-within:text-primary transition-colors" />
                    <input
                      type="email"
                      placeholder="E-Posta"
                      className="input pl-11 py-3 bg-bgAlt/50 focus:bg-card border-borderColor/50 rounded-xl w-full text-sm font-medium transition-all"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-text/70 uppercase tracking-wider ml-1">Yeni Şifre</label>
                  <div className="relative group">
                    <Lock className="absolute left-3.5 top-3.5 w-5 h-5 text-text/40 group-focus-within:text-primary transition-colors" />
                    <input
                      type="password"
                      placeholder="Değiştirmek istemiyorsanız boş bırakın"
                      className="input pl-11 py-3 bg-bgAlt/50 focus:bg-card border-borderColor/50 rounded-xl w-full text-sm font-medium transition-all"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                </div>

                <IconTextButton 
                  type="submit" 
                  icon={ShieldCheck} 
                  className="btn-primary mt-2 py-3 rounded-xl shadow-lg shadow-primary/20 w-full justify-center text-sm font-bold"
                  disabled={loading}
                >
                  Bilgileri Güncelle
                </IconTextButton>
              </form>
            </div>
          </div>
        </div>

        {/* Sağ Kolon: Yazdırma Ayarları */}
        <div className="lg:col-span-8 xl:col-span-9 w-full -mt-6">
          <AccountPrintSettings />
        </div>
      </div>
    </div>
  );
};
